/**
 * Event triggers: one row per kind of event a flow can be started by, each holding the filter that
 * decides which events of that kind match.
 *
 * They share a shape — a flow, an organization, a version target, and whatever narrows the event —
 * and each kind's endpoints live under `/triggers`.
 */
import { OneOrManySchema } from 'notable-common';
import { z } from 'zod';
import { FlowTriggerVersionTarget } from '../flow/versionTarget.js';
import { FlowRpaJobKind } from '../rpaJob/rpaJob.js';
/** Which moment in a form's life the trigger listens for. */
export var FlowFormEventType;
(function (FlowFormEventType) {
    FlowFormEventType["Completion"] = "Completion";
    FlowFormEventType["Started"] = "Started";
})(FlowFormEventType || (FlowFormEventType = {}));
/** How the job a trigger is waiting on finished. */
export var FlowJobTerminalStatus;
(function (FlowJobTerminalStatus) {
    FlowJobTerminalStatus["Success"] = "Success";
    FlowJobTerminalStatus["Failed"] = "Failed";
})(FlowJobTerminalStatus || (FlowJobTerminalStatus = {}));
/** Path parameters the endpoints addressing one event trigger accept, whatever its kind. */
export const FlowEventTriggerIdParamsSchema = z.object({
    id: z.uuid().describe('The trigger.'),
});
/** The fields every event trigger is created with, whatever it listens to. */
const EventTriggerTargetSchema = z.object({
    flowId: z.uuid().describe('Flow the trigger starts.'),
    organizationId: z.uuid().describe('Organization whose events the trigger listens to.'),
    versionTarget: z
        .enum(FlowTriggerVersionTarget)
        .describe('Which definition of the flow a matching event runs.')
        .optional(),
});
/** Bodies `POST /triggers/hl7-message-events` accepts. */
export const FlowHl7MessageEventTriggerCreateBodySchema = EventTriggerTargetSchema.extend({
    messageType: z
        .string()
        .nullable()
        .describe('Match only this message type, such as ADT.')
        .optional(),
    messageTrigger: z
        .string()
        .nullable()
        .describe('Match only this trigger event, such as A08.')
        .optional(),
    sendingFacility: z.string().nullable().describe('Match only this sending facility.').optional(),
});
export const FlowHl7MessageEventTriggerUpdateBodySchema = FlowHl7MessageEventTriggerCreateBodySchema.omit({ flowId: true, organizationId: true }).partial();
export const FlowHl7MessageEventTriggerListQuerySchema = z.object({
    messageType: z.string().optional(),
    messageTrigger: z.string().optional(),
    sendingFacility: z.string().optional(),
    flowId: z.uuid().describe('Return only triggers starting this flow.').optional(),
    organizationId: OneOrManySchema(z.uuid()).optional(),
});
/** Bodies `POST /triggers/x12-message-events` accepts. */
export const FlowX12MessageEventTriggerCreateBodySchema = EventTriggerTargetSchema.extend({
    interchangeSenderId: z.string().nullable().describe('Match only this sender.').optional(),
    transactionSetIdCode: z
        .string()
        .nullable()
        .describe('Match only this transaction set.')
        .optional(),
    transactionSetPurposeCode: z.string().nullable().describe('Match only this purpose.').optional(),
});
export const FlowX12MessageEventTriggerUpdateBodySchema = FlowX12MessageEventTriggerCreateBodySchema.omit({ flowId: true, organizationId: true }).partial();
export const FlowX12MessageEventTriggerListQuerySchema = z.object({
    interchangeSenderId: z.string().optional(),
    transactionSetIdCode: z.string().optional(),
    transactionSetPurposeCode: z.string().optional(),
    flowId: z.uuid().describe('Return only triggers starting this flow.').optional(),
    organizationId: OneOrManySchema(z.uuid()).optional(),
});
/** Bodies `POST /triggers/sftp-message-events` accepts. */
export const FlowSftpMessageEventTriggerCreateBodySchema = EventTriggerTargetSchema.extend({
    fileType: z.string().nullable().describe('Match only files of this type.').optional(),
});
export const FlowSftpMessageEventTriggerUpdateBodySchema = FlowSftpMessageEventTriggerCreateBodySchema.omit({
    flowId: true,
    organizationId: true,
}).partial();
export const FlowSftpMessageEventTriggerListQuerySchema = z.object({
    fileType: z.string().optional(),
    flowId: z.uuid().describe('Return only triggers starting this flow.').optional(),
    organizationId: OneOrManySchema(z.uuid()).optional(),
});
/** Bodies `POST /triggers/job-completion-events` accepts. */
export const FlowJobCompletionEventTriggerCreateBodySchema = EventTriggerTargetSchema.extend({
    jobKind: z.enum(FlowRpaJobKind).nullable().describe('Match only jobs of this kind.').optional(),
    terminalStatus: z
        .enum(FlowJobTerminalStatus)
        .nullable()
        .describe('Match only jobs that ended this way.')
        .optional(),
});
export const FlowJobCompletionEventTriggerUpdateBodySchema = FlowJobCompletionEventTriggerCreateBodySchema.omit({
    flowId: true,
    organizationId: true,
}).partial();
export const FlowJobCompletionEventTriggerListQuerySchema = z.object({
    jobKind: z.enum(FlowRpaJobKind).optional(),
    terminalStatus: z.enum(FlowJobTerminalStatus).optional(),
    flowId: z.uuid().describe('Return only triggers starting this flow.').optional(),
    organizationId: OneOrManySchema(z.uuid()).optional(),
});
/**
 * Bodies `POST /triggers/form-events` accepts.
 *
 * A trigger may name several templates or none, in which case every form of that type matches.
 * `previsitFormTemplateId` is the one-template spelling the endpoint has always also taken.
 */
export const FlowFormEventTriggerCreateBodySchema = EventTriggerTargetSchema.extend({
    type: z.enum(FlowFormEventType).describe('Which moment in the form to listen for.'),
    previsitFormTemplateId: z.uuid().nullable().optional(),
    previsitFormTemplateIds: z.array(z.uuid()).optional(),
});
export const FlowFormEventTriggerUpdateBodySchema = FlowFormEventTriggerCreateBodySchema.omit({
    flowId: true,
    organizationId: true,
}).partial();
export const FlowFormEventTriggerListQuerySchema = z.object({
    type: z.enum(FlowFormEventType).optional(),
    previsitFormTemplateId: z.uuid().optional(),
    flowId: z.uuid().describe('Return only triggers starting this flow.').optional(),
    organizationId: OneOrManySchema(z.uuid()).optional(),
});
/**
 * Bodies `POST /triggers/voice-events` accepts.
 *
 * The phone number is the trigger: one number starts one flow, which is why the update body may
 * move a trigger between organizations where the other kinds may not.
 */
export const FlowVoiceEventTriggerCreateBodySchema = EventTriggerTargetSchema.extend({
    phoneNumber: z.string().describe('The number that starts the flow when it is called.'),
    assistantId: z.uuid().nullish().describe('Assistant that answers.'),
    fallbackDestination: z.string().nullish().describe('Where calls go when the flow cannot run.'),
});
export const FlowVoiceEventTriggerUpdateBodySchema = FlowVoiceEventTriggerCreateBodySchema.partial();
export const FlowVoiceEventTriggerListQuerySchema = z.object({
    flowId: z.uuid().describe('Return only triggers starting this flow.').optional(),
    organizationId: OneOrManySchema(z.uuid()).optional(),
    phoneNumber: z.string().describe('Return only the trigger on this number.').optional(),
    assistantId: z.uuid().nullish().describe('Return only triggers answered by this assistant.'),
});
//# sourceMappingURL=eventTriggers.js.map